import {Component, OnDestroy, OnInit} from '@angular/core';
import * as moment from 'moment';
import {AlertController, ModalController} from '@ionic/angular';
import {ActivatedRoute, Router} from '@angular/router';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Subscription, timer, throwError} from 'rxjs';
import {switchMap, tap, takeWhile, catchError} from 'rxjs/operators';

import {RepositoryService} from '../../../smoothr-web-app-core/services/repository/repository.service';
import {HomePage} from '../home/home.page';
import {Api} from '../../../smoothr-web-app-core/api/api';
import Venue from '../../../smoothr-web-app-core/models/Venue';
import {PreorderType} from '../../../smoothr-web-app-core/enums/PreorderType';
import {TranslateService} from '@ngx-translate/core';
import {OrderUtils} from '../../../smoothr-web-app-core/utils/order-utils';
import {environment} from '../../../environments/environment';
import {AnalyticsService} from '../../../smoothr-web-app-core/services/analytics/analytics.service';
import {numberD, numberToCurrency, sleep} from '../../../smoothr-web-app-core/utils/utils';
import RepositoryDirective from '../../../smoothr-web-app-core/directives/repository-directive';
import {RequestEmailComponent} from './request-email/request-email.component';
import Payment from 'src/smoothr-web-app-core/models/Payment';

import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from '../../../assets/pdfmake/vfs_fonts/vfs_fonts';

pdfMake.vfs = pdfFonts;

@Component({
	selector: 'app-payment-success',
	templateUrl: './payment-success.page.html',
	styleUrls: ['./payment-success.page.scss']
})
export class PaymentSuccessPage extends RepositoryDirective implements OnInit, OnDestroy {
	static url = 'payment/success';
	static urlWithPaymentParam = 'payment/success/:payment';
	static ORDER_ID_PARAM = 'order';

	moment = moment;
	OrderUtils = OrderUtils;
	pt = PreorderType;

	orderVenue: Venue;
	error: any;
	orderId: string;
	environment = environment;
	numberToCurrency = numberToCurrency;
	loading = true;
	tip: number = 0;
	payment: Payment;

	private subscriptions: Subscription = new Subscription();

	constructor(
		private translate: TranslateService,
		protected repository: RepositoryService,
		private alertCtrl: AlertController,
		private router: Router,
		private snackbarCtrl: MatSnackBar,
		private route: ActivatedRoute,
		private analytics: AnalyticsService,
		private modalCtrl: ModalController
	) {
		super(repository);
	}

	static async navigate(router: Router, order: string, payment: string) {
		await router.navigate([this.url + '/' + payment], {queryParams: {order}});
	}

	ngOnInit() {
		super.ngOnInit();
		this.route.queryParams.subscribe(params => {
			console.log({
				params
			});
			if (this.verifiedOrder && this.verifiedOrder._id && !params.order) {
				this.orderId = this.verifiedOrder._id;
			} else {
				this.orderId = params.order;
			}
			this.reloadOrder();
		});
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	async reloadOrder(attempt: number = 0, error: any = null) {
		this.error = null;
		this.loading = true;

		if (attempt > 10) {
			this.error = error ? error : this.translate.instant('payment_success_page.payment_error');
			this.snackbarCtrl.open(this.error, null, {duration: 2000});
			this.loading = false;
			return;
		}

		if (attempt > 0) {
			await sleep(200);
		}

		try {
			const orderResponse = await Api.getOrder(this.orderId);
			console.log('orderResponse', orderResponse.data);

			this.repository.verifiedOrder.emit(orderResponse.data);
			console.log(this.verifiedOrder?.isPayed);
			if (!this.verifiedOrder?.isPayed) {
				await this.reloadOrder(attempt + 1, this.translate.instant('payment_success_page.please_reload'));
				return;
			}

			const paymentId = this.route.snapshot.paramMap.get('payment');
			console.log('paymentId', paymentId);

			if (paymentId) {
				const pollSub = this.waitForReceipt$(paymentId).subscribe({
					complete: () => {
						if (this.payment && !this.payment.posCheckoutReceipt) {
							this.openRequestEmail();
						}
					},
					error: err => console.error('Polling error:', err)
				});
				this.subscriptions.add(pollSub);
			}

			this.analytics.paymentSuccess();

			if (this.venue && this.venue._id === this.verifiedOrder.venue) {
				this.orderVenue = this.venue;
			} else {
				this.orderVenue = (await Api.getLazyVenue(this.verifiedOrder.venue)).data;
			}

			this.error = false;
			this.loading = false;
		} catch (e) {
			console.error(e);
			await this.reloadOrder(attempt + 1, e);
		}
	}

	home() {
		if (this.loading) return;
		if (this.error) {
			this.reloadOrder();
			return;
		}
		this.loading = true;
		HomePage.navigate(this.router, this.repository);
	}

	async openRequestEmail() {
		await RequestEmailComponent.show(this.modalCtrl, this.payment, this.orderVenue);
	}

	waitForReceipt$(paymentId: string) {
		const intervalMs = 10000;
		const timeoutMs = 60000;
		const startTime = Date.now();

		return timer(0, intervalMs).pipe(
			switchMap(() => Api.getPayment(paymentId).then(res => res.data)),
			tap(payment => {
				this.payment = payment;
				this.tip = numberD(payment.tip);
			}),
			takeWhile(payment => {
				const receiptExists = !!payment.posCheckoutReceipt;
				const timeoutReached = Date.now() - startTime > timeoutMs;
				return !receiptExists && !timeoutReached;
			}, true),
			catchError(err => {
				console.error('Polling failed:', err);
				return throwError('Failed to retrieve receipt');
			})
		);
	}

	generatePdf() {
		if (!this.payment?.posCheckoutReceipt) return;

		pdfMake.fonts = {
			Courier: {
				normal: 'CourierPrime-Regular.ttf',
				bold: 'CourierPrime-Bold.ttf',
				italics: 'CourierPrime-Italic.ttf',
				bolditalics: 'CourierPrime-BoldItalic.ttf'
			}
		};

		const docDefinition = {
			content: [
				{
					columns: [
						{width: '*', text: ''},
						{width: 'auto', text: this.payment.posCheckoutReceipt, alignment: 'center'},
						{width: '*', text: ''}
					],
					fontSize: 9,
					font: 'Courier',
					lineHeight: 1
				}
			],
			pageSize: 'A5',
			pageMargins: [10, 10, 10, 10]
		};

		pdfMake.createPdf(docDefinition).open();
	}
}
